import React, { useMemo, useState } from 'react';
import { absScreenBody, screenCached, deskAbsStructure } from './absCache.js';
import { IRR_NOMINAL_LABEL, effectiveAnnualFromNominal, evaluateAllScenarios } from '../engine/dealEngine.js';
import {
  buildScenarioFreeze,
  contentHash,
  downloadText,
  loadRegister,
  upsertNamedScenario,
} from '../engine/scenarioFreeze.js';
import { createReleaseReadiness, evaluateReleaseReadiness } from '../engine/releaseGate.js';
import { downloadBidLetterDocx, downloadIcMemoDocx, downloadOnePagerDocx } from '../engine/capitalDocx.js';
import { downloadUnderwritingXlsx } from '../engine/underwritingXlsx.js';
import { createBidProposal, validateBidProposal } from '../engine/bidProposal.js';
import { track } from '../lib/track.js';
import { useAuth } from '../auth/AuthContext.jsx';
import {
  CASE_ORDER,
  deskOverrides,
  evaluateStress,
  fmtMM,
  fmtPct,
  leaseLabel,
  packageFacts,
  valueCases,
} from './model.js';
import { Loading, Src, useAssistantSummary, useMeeting } from './common.jsx';
import { checklistForAssistant } from '../diligence/checklist.js';
import ReadinessChip from '../diligence/ReadinessChip.jsx';
import { useChecklist } from '../diligence/useChecklist.js';

const RELEASE_ROWS = [
  { category: 'concentration', label: 'Concentration in one well disclosed' },
  { category: 'title_midstream', label: 'Midstream / title status disclosed' },
  { category: 'aro_pna', label: 'Plug & abandonment (ARO) treatment stated' },
  { category: 'overhead_treatment', label: 'Buyer G&A treatment stated' },
  { category: 'operating_continuity', label: 'Operating continuity status disclosed' },
];

function draftRelease(category, ctx) {
  const { facts, diligence, desk, owner, asOf, baseCase } = ctx;
  const well = facts?.topAsset ? leaseLabel(facts.topAsset.lease) : 'Largest well';
  const common = { category, owner, asOf, disposition: 'disclosed' };
  switch (category) {
    case 'concentration':
      return {
        ...common,
        sourceRef: 'dealPackage.assets',
        classification: 'model',
        summary: `${well} is ${fmtPct(facts?.topShare, 0)} seller-basis share of PDP PV10. Values shown on the "${baseCase}" forecast case.`,
      };
    case 'title_midstream': {
      const m = diligence?.midstreamAssignability;
      const open = m?.contracts?.filter((c) => /open/i.test(c.status || '')).length;
      return {
        ...common,
        sourceRef: 'diligence.midstreamAssignability',
        classification: 'judgment',
        summary: m
          ? `Midstream assignability ${m.overallStatus || 'open'}${open != null ? ` (${open} of ${m.contracts.length} contracts open)` : ''}; treated as a pricing contingency.`
          : 'Midstream and title status not yet diligenced; disclosed as open.',
      };
    }
    case 'aro_pna':
      return {
        ...common,
        sourceRef: 'desk.aro',
        classification: 'judgment',
        summary: `ARO of ${fmtMM(desk.aroMM, 2)} for ${facts?.negativeWells ?? 'the'} negative-PV wells.`,
        treatment: 'Funded reserve, deducted from PV and collateral.',
      };
    case 'overhead_treatment':
      return {
        ...common,
        sourceRef: 'engine.buyerOverhead',
        classification: 'judgment',
        summary: 'Buyer G&A is an illustrative ~$53K/mo, not a seller fact.',
        treatment: 'Excluded from base cash flow; included in the stress case.',
      };
    case 'operating_continuity':
      return {
        ...common,
        sourceRef: 'desk.operatingContinuity',
        classification: 'judgment',
        summary: 'Operator transition and field staffing not yet diligenced; disclosed as open.',
      };
    default:
      return common;
  }
}

function FormRow({ label, children }) {
  return (
    <label className="mp-field" style={{ flex: '1 1 220px' }}>
      {label}
      {children}
    </label>
  );
}

export default function PackageStep({
  dealId,
  dealPackage,
  dealPackageFailed,
  diligence,
  executive,
  reserves,
  wells,
  land,
  map,
  geoscience,
  onNavigate,
}) {
  const auth = useAuth();
  const { desk, patchDesk, forecast } = useMeeting({ dealId, dealPackage });
  const { result: checklist } = useChecklist({
    dealId,
    pkg: dealPackage,
    diligence,
    wells,
    land,
    map,
    geoscience,
  });
  const [caseId, setCaseId] = useState('base');
  const [msg, setMsg] = useState({});
  const [busy, setBusy] = useState(null);
  const owner = auth.user?.name || 'Desk';
  const facts = useMemo(
    () => (dealPackage ? packageFacts(dealPackage, { executive, reserves }) : null),
    [dealPackage, executive, reserves],
  );
  const rows = useMemo(
    () => (dealPackage ? valueCases(dealPackage, forecast, desk) : []),
    [dealPackage, forecast, desk],
  );
  const byId = Object.fromEntries(rows.map((r) => [r.id, r]));
  const chosen = byId[caseId] || byId.base || byId.seller;
  const asOf = dealPackage?.meta?.generatedAt || dealPackage?.meta?.priceDeck || '';

  const readiness = useMemo(() => {
    const base = createReleaseReadiness({ asOf });
    const saved = desk.release || {};
    return {
      ...base,
      items: base.items.map((it) => (saved[it.category] ? { ...it, ...saved[it.category] } : it)),
    };
  }, [desk.release, asOf]);
  const gate = useMemo(() => evaluateReleaseReadiness(readiness, { asOf }), [readiness, asOf]);
  const readinessHash = useMemo(() => contentHash(readiness), [readiness]);

  const proposal = useMemo(
    () =>
      createBidProposal({
        meta: dealPackage?.meta || {},
        prior: desk.proposal || null,
        bidPriceMM: desk.bidPriceMM,
      }),
    [dealPackage, desk.proposal, desk.bidPriceMM],
  );
  const proposalCheck = useMemo(
    () => validateBidProposal({ ...proposal, purchasePriceMM: desk.bidPriceMM }),
    [proposal, desk.bidPriceMM],
  );

  const frozen = useMemo(() => {
    if (!desk.frozen?.scenarioId) return null;
    return loadRegister().find((f) => f.scenarioId === desk.frozen.scenarioId) || null;
    // re-read when the desk's frozen pointer changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desk.frozen?.scenarioId]);
  const stale =
    frozen &&
    (desk.frozen?.readinessHash !== readinessHash ||
      desk.frozen?.caseId !== caseId ||
      frozen.assumptions?.bidPriceMM !== desk.bidPriceMM ||
      (frozen.assumptions?.aroMM ?? 0) !== (desk.aroMM ?? 0) ||
      // Editing "Your case" on Forecast changes the exported numbers too.
      (desk.frozen?.customHash ?? 'null') !== JSON.stringify(desk.custom ?? null));

  useAssistantSummary(
    chosen
      ? {
          surface: 'package',
          case: chosen.kase.label,
          bidPriceMM: desk.bidPriceMM,
          bidEdited: desk.bidEdited === true,
          frozen: frozen ? frozen.label : null,
          releaseReady: gate.ready,
          releaseBlockers: gate.blockers,
          bidLetterBlockers: proposalCheck.blockers,
          checklist: checklistForAssistant(checklist),
        }
      : null,
  );

  if (!dealPackage) {
    return dealPackageFailed ? (
      <div className="mp">
        <div className="mp-empty">No deal package is loaded for this deal on this host yet.</div>
      </div>
    ) : (
      <Loading label="Loading the package…" />
    );
  }

  const setNote = (k, text, ok = false) => setMsg((m) => ({ ...m, [k]: text ? { text, ok } : null }));

  function logSent(what, f) {
    patchDesk((prev) => ({
      sent: [
        { what, label: f?.label || '', hash: f?.contentHash || '', at: new Date().toISOString(), by: owner },
        ...(prev.sent || []),
      ].slice(0, 30),
    }));
  }

  function freezeCase() {
    if (!chosen) return;
    const overrides = { ...deskOverrides(dealPackage, desk), boswellVolAdj: chosen.kase.volAdj || 0 };
    const stress = evaluateStress(dealPackage, { ...desk });
    const scenarioRows = evaluateAllScenarios(dealPackage, overrides).map((s) => ({
      id: s.id,
      scenario: s.label,
      pv: +s.result.pvMM.toFixed(2),
      ntm: +s.result.ntmCfMM.toFixed(2),
      irr: s.result.irrAnnual,
      bridge: +s.result.bidBridgePvMM.toFixed(2),
      x5: +s.result.multiples.x5.toFixed(2),
    }));
    const label = `${chosen.kase.label} · bid ${fmtMM(desk.bidPriceMM, 1)}`;
    const f = buildScenarioFreeze({
      pkg: dealPackage,
      assumptions: overrides,
      label,
      note: chosen.kase.plain || '',
      base: chosen.result,
      stress,
      scenarioRows,
      approver: owner,
      midstream: diligence?.midstreamAssignability || null,
      dca: forecast?.dca || null,
      releaseReadiness: readiness,
      proposal: { ...proposal, purchasePriceMM: desk.bidPriceMM },
    });
    upsertNamedScenario(f);
    patchDesk({ frozen: { scenarioId: f.scenarioId, caseId, readinessHash, label, customHash: JSON.stringify(desk.custom ?? null) } });
    // A new freeze supersedes every per-export note (stale / blocked / downloaded) from the prior case.
    setMsg({ freeze: { text: `Frozen "${label}" (${f.contentHash.slice(0, 8)}). Every export below comes from this case.`, ok: true } });
    track('scenario_saved', { content_hash: f.contentHash, label, source: 'meeting_package' });
  }

  function guardFrozen(key) {
    if (!frozen) {
      setNote(key, 'Freeze a case first.');
      return false;
    }
    if (stale) {
      setNote(key, 'The case, bid, ARO or checklist changed since the freeze. Freeze again first.');
      return false;
    }
    return true;
  }

  function exportBidLetter() {
    if (!guardFrozen('bid')) return;
    const check = validateBidProposal({ ...proposal, purchasePriceMM: desk.bidPriceMM });
    if (!check.ok) {
      setNote('bid', `Missing: ${check.blockers.join('; ')}.`);
      return;
    }
    downloadBidLetterDocx(frozen, { ...proposal, purchasePriceMM: desk.bidPriceMM });
    logSent('Bid letter (.docx)', frozen);
    setNote('bid', 'Downloaded. Put it on letterhead as a PDF before sending.', true);
    track('bid_letter_exported', { content_hash: frozen.contentHash, source: 'meeting_package' });
  }

  function exportOnePager() {
    if (!guardFrozen('one')) return;
    const g = evaluateReleaseReadiness(frozen.releaseReadiness, { asOf: frozen.asOf || '' });
    if (!g.ready) {
      setNote('one', `Blocked until the checklist is complete (${g.blockers.length} open).`);
      return;
    }
    try {
      downloadOnePagerDocx(frozen);
      logSent('Capital one-pager (.docx)', frozen);
      setNote('one', 'Downloaded.', true);
      track('capital_one_pager_exported', { content_hash: frozen.contentHash, source: 'meeting_package' });
    } catch (e) {
      setNote('one', e.message || 'Export blocked.');
    }
  }

  function exportMemo() {
    if (!guardFrozen('memo')) return;
    downloadIcMemoDocx(frozen);
    logSent('Investment memo (.docx)', frozen);
    setNote('memo', 'Downloaded.', true);
  }

  function exportModel() {
    if (!guardFrozen('xlsx')) return;
    downloadUnderwritingXlsx(frozen);
    logSent('Underwriting model (.xlsx)', frozen);
    setNote('xlsx', 'Downloaded.', true);
  }

  async function exportTape() {
    if (!guardFrozen('tape')) return;
    if (!desk.aroSet) {
      setNote('tape', 'Set ARO on Value first. Lenders need it to size the note.');
      return;
    }
    setBusy('tape');
    try {
      const res = await screenCached(absScreenBody(dealId, deskAbsStructure(desk), desk));
      const b = res.base || res;
      const tape = {
        schema: 'd2d.capital-tape.v1',
        dealId,
        package: dealPackage.meta?.package || null,
        frozenCase: { label: frozen.label, scenarioId: frozen.scenarioId, contentHash: frozen.contentHash },
        generatedAt: new Date().toISOString(),
        basis: 'Sponsor assumptions, not counterparty-confirmed. Not a financing commitment, rating or offer.',
        proposedPurchasePriceMM: desk.bidPriceMM,
        structure: b.structure,
        headline: b.summary?.headline || b.solve,
        downside: res.downside?.summary?.headline || null,
        coverageByMonth: (b.coverage?.months || []).map((m) => ({ month: m.date, cf: m.cf, debtService: m.debtService, dscr: m.dscr })),
        collateralExclusions: b.collateral?.excluded || [],
        hedges: b.hedges || [],
        openGaps: b.screenGaps || [],
      };
      downloadText(`${dealId}-capital-tape.json`, JSON.stringify(tape, null, 2), 'application/json');
      const csv = ['month,cash_flow,debt_service,dscr', ...tape.coverageByMonth.map((m) => `${m.month},${m.cf},${m.debtService},${m.dscr}`)].join('\n');
      downloadText(`${dealId}-coverage-by-month.csv`, csv, 'text/csv');
      logSent('Capital tape (.json + coverage .csv)', frozen);
      setNote('tape', 'Downloaded tape and month-by-month coverage.', true);
    } catch (e) {
      setNote('tape', `ABS engine error: ${e.message || e}`);
    } finally {
      setBusy(null);
    }
  }

  function toggleRelease(category) {
    const saved = { ...(desk.release || {}) };
    if (saved[category]) {
      delete saved[category];
    } else {
      saved[category] = draftRelease(category, {
        facts,
        diligence,
        desk,
        owner,
        asOf,
        baseCase: chosen?.kase?.label || 'Our base',
      });
    }
    patchDesk({ release: saved });
  }

  const p = desk.proposal || {};
  const contact = (p.contacts && p.contacts[0]) || {};
  const patchProposal = (patch) =>
    patchDesk((prev) => ({ proposal: { ...(prev.proposal || {}), ...patch } }));
  const patchContact = (patch) =>
    patchProposal({ contacts: [{ role: 'Primary', ...contact, ...patch }] });
  const disclosedCount = RELEASE_ROWS.filter((r) => desk.release?.[r.category]).length;

  return (
    <div className="mp">
      <div className="mp-head">
        <div>
          <span className="mp-eyebrow">Package · everything that leaves the building comes from one frozen case</span>
          <h2 className="mp-h2">Freeze a case, then send what each audience needs</h2>
        </div>
      </div>

      <section className="mp-freeze" aria-label="Case to send">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div className="eyebrow">CASE TO SEND</div>
          <label style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 15, color: '#D4DEE5' }}>
            Case
            <select value={caseId} onChange={(e) => setCaseId(e.target.value)}>
              {CASE_ORDER.filter((id) => byId[id]).map((id) => (
                <option key={id} value={id}>
                  {byId[id].kase.label} · bid {fmtMM(desk.bidPriceMM, 1)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="stats">
          <div>
            <div className="v">{fmtMM(chosen?.pv10MM, 2)}</div>
            <div className="k">PDP PV10</div>
          </div>
          <div>
            <div className="v" title={chosen?.irr == null ? IRR_NOMINAL_LABEL : `${IRR_NOMINAL_LABEL}. Effective annual ${fmtPct(chosen.irrEffective ?? effectiveAnnualFromNominal(chosen.irr), 2)}.`}>
              {chosen?.irr == null ? 'n/m' : fmtPct(chosen.irr)}
            </div>
            <div className="k">{IRR_NOMINAL_LABEL}</div>
            {chosen?.irr != null ? (
              <div className="mp-note">effective {fmtPct(chosen.irrEffective ?? effectiveAnnualFromNominal(chosen.irr), 2)}</div>
            ) : null}
          </div>
          <div>
            <div className="v">{fmtMM(chosen?.afterAroMM, 2)}</div>
            <div className="k">After ARO</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
          <button type="button" className="mp-btn amber" onClick={freezeCase}>
            {frozen && !stale ? 'Freeze again' : 'Freeze case'}
          </button>
          {frozen ? (
            <span style={{ fontSize: 13, color: stale ? '#F2C77A' : '#B9C8D3' }}>
              {stale ? 'Changed since freeze' : `Frozen: ${frozen.label}`}
            </span>
          ) : null}
        </div>
      </section>
      {msg.freeze ? (
        <div className={`mp-note`} role="status" style={{ color: msg.freeze.ok ? 'var(--mp-green)' : 'var(--mp-amber-ink)' }}>
          {msg.freeze.text}
        </div>
      ) : null}

      <section className="mp-card" aria-label="Diligence checklist" data-testid="package-checklist">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <h3 className="mp-h3">Diligence checklist</h3>
          <ReadinessChip
            pct={checklist?.readiness?.pre_offer?.pct}
            complete={checklist?.readiness?.pre_offer?.complete}
            partial={checklist?.readiness?.pre_offer?.partial}
            required={checklist?.readiness?.pre_offer?.required}
            overdue={checklist?.warnings?.overdue}
          />
        </div>
        {!checklist ? (
          <div className="mp-note">Loading the checklist…</div>
        ) : checklist.warnings.preOfferMissingRequired > 0 ? (
          <div className="mp-note" role="status" data-testid="checklist-warning" style={{ color: 'var(--mp-amber-ink)' }}>
            Warning: {checklist.warnings.preOfferMissingRequired} required pre-offer items are still missing
            {checklist.warnings.overdue ? ` and ${checklist.warnings.overdue} dated tasks are overdue` : ''}. This does not block the package.
          </div>
        ) : (
          <div className="mp-note">Required pre-offer items are in hand or partial. Nothing is gated.</div>
        )}
        <div>
          {(checklist?.gaps || [])
            .filter((i) => i.phase === 'pre_offer' && i.requiredForPhase && i.status === 'Missing')
            .slice(0, 8)
            .map((i) => (
              <div key={i.id} className="mp-list-item">
                <span className="mp-badge fail" style={{ minWidth: 72 }}>Missing</span>
                <div className="body">
                  <div className="title" style={{ fontSize: 15 }}>{i.item}</div>
                  <div className="text">{i.owner} · {i.whatSatisfies}</div>
                </div>
              </div>
            ))}
        </div>
        <button type="button" className="mp-link" onClick={() => onNavigate('checklist')}>
          Open the checklist →
        </button>
      </section>

      <div className="mp-row">
        <section className="mp-card mp-grow-3" aria-label="Before anything goes out">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <h3 className="mp-h3">Before anything goes out</h3>
            <span className="mp-note">
              {disclosedCount} of {RELEASE_ROWS.length} disclosed · required for the capital one-pager
            </span>
          </div>
          <div>
            {RELEASE_ROWS.map((r) => {
              const item = desk.release?.[r.category];
              const needsAro = r.category === 'aro_pna' && !desk.aroSet;
              return (
                <div key={r.category} className="mp-list-item" style={{ alignItems: 'center' }}>
                  <span className={item ? 'ok' : 'no'} style={{ fontWeight: 700, color: item ? 'var(--mp-green)' : 'var(--mp-red)', width: 16 }}>
                    {item ? '✓' : '○'}
                  </span>
                  <div className="body">
                    <div className="title" style={{ fontSize: 15 }}>{r.label}</div>
                    {item ? (
                      <div className="text">
                        {item.summary}
                        {item.treatment ? ` ${item.treatment}` : ''} <Src kind={item.classification === 'model' ? 'model' : 'judgment'} />
                      </div>
                    ) : needsAro ? (
                      <div className="text">Set ARO on Value first.</div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="mp-btn ghost small"
                    disabled={needsAro && !item}
                    onClick={() => toggleRelease(r.category)}
                  >
                    {item ? 'Undo' : 'Disclose'}
                  </button>
                </div>
              );
            })}
            <div className="mp-list-item" style={{ alignItems: 'center' }}>
              <span style={{ fontWeight: 700, color: desk.forecastApproved ? 'var(--mp-green)' : 'var(--mp-amber-ink)', width: 16 }}>
                {desk.forecastApproved ? '✓' : '!'}
              </span>
              <div className="body">
                <div className="title" style={{ fontSize: 15 }}>Forecast reviewed</div>
                <div className="text">
                  {desk.forecastApproved ? 'Our base forecast is approved.' : 'Our base forecast is not approved yet.'}
                </div>
              </div>
              <button type="button" className="mp-btn ghost small" onClick={() => onNavigate('forecast')}>
                Review
              </button>
            </div>
          </div>
        </section>

        <section className="mp-card mp-grow-2" aria-label="Bid letter details">
          <h3 className="mp-h3">Bid letter details</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <FormRow label="Bidding entity (letterhead)">
              <input
                style={{ width: '100%', fontFamily: 'inherit' }}
                value={p.letterheadEntity ?? proposal.letterheadEntity ?? ''}
                onChange={(e) => patchProposal({ letterheadEntity: e.target.value })}
              />
            </FormRow>
            <FormRow label="Financing sources">
              <input
                style={{ width: '100%', fontFamily: 'inherit' }}
                placeholder="e.g. equity commitments + acquisition facility"
                value={p.financingSources || ''}
                onChange={(e) => patchProposal({ financingSources: e.target.value })}
              />
            </FormRow>
            <FormRow label="Financing certainty through closing">
              <input
                style={{ width: '100%', fontFamily: 'inherit' }}
                placeholder="how committed the funds are"
                value={p.financingCertainty || ''}
                onChange={(e) => patchProposal({ financingCertainty: e.target.value })}
              />
            </FormRow>
            <FormRow label="Contact name">
              <input style={{ width: '100%', fontFamily: 'inherit' }} value={contact.name || ''} onChange={(e) => patchContact({ name: e.target.value })} />
            </FormRow>
            <FormRow label="Contact email">
              <input style={{ width: '100%', fontFamily: 'inherit' }} value={contact.email || ''} onChange={(e) => patchContact({ email: e.target.value })} />
            </FormRow>
            <FormRow label="Contact phone">
              <input style={{ width: '100%', fontFamily: 'inherit' }} value={contact.office || ''} onChange={(e) => patchContact({ office: e.target.value })} />
            </FormRow>
          </div>
          <div className="mp-note">
            {proposalCheck.ok ? (
              <span style={{ color: 'var(--mp-green)' }}>✓ Bid letter is complete.</span>
            ) : (
              <>Still needed: {proposalCheck.blockers.join('; ')}.</>
            )}
          </div>
        </section>
      </div>

      <section className="mp-outputs" aria-label="Outputs">
        <div className="mp-output">
          <div className="aud">For the seller / advisor</div>
          <h3>Bid letter</h3>
          <p>Price, effective date, contingencies and contacts, ready for letterhead.</p>
          {msg.bid ? <div className={`msg ${msg.bid.ok ? 'ok' : ''}`}>{msg.bid.text}</div> : null}
          <div className="acts">
            <button type="button" className="mp-btn small" onClick={exportBidLetter}>
              Download .docx
            </button>
          </div>
        </div>
        <div className="mp-output">
          <div className="aud">For equity partners</div>
          <h3>Capital one-pager + memo</h3>
          <p>The analysis, forecast and value story with every disclosure stated.</p>
          {msg.one ? <div className={`msg ${msg.one.ok ? 'ok' : ''}`}>{msg.one.text}</div> : null}
          {msg.memo ? <div className={`msg ${msg.memo.ok ? 'ok' : ''}`}>Memo: {msg.memo.text}</div> : null}
          <div className="acts">
            <button type="button" className="mp-btn small" onClick={exportOnePager}>
              One-pager .docx
            </button>
            <button type="button" className="mp-btn ghost small" onClick={exportMemo}>
              Memo .docx
            </button>
          </div>
        </div>
        <div className="mp-output">
          <div className="aud">For engineers / analysts</div>
          <h3>Underwriting model</h3>
          <p>Assumptions, cases, scenarios and the liability schedule in an editable workbook.</p>
          {msg.xlsx ? <div className={`msg ${msg.xlsx.ok ? 'ok' : ''}`}>{msg.xlsx.text}</div> : null}
          <div className="acts">
            <button type="button" className="mp-btn small" onClick={exportModel}>
              Download .xlsx
            </button>
          </div>
        </div>
        <div className="mp-output">
          <div className="aud">For lenders / note buyers</div>
          <h3>Capital tape</h3>
          <p>Structure, coverage by month, collateral exclusions and open gaps, marked as sponsor assumptions.</p>
          {msg.tape ? <div className={`msg ${msg.tape.ok ? 'ok' : ''}`}>{msg.tape.text}</div> : null}
          <div className="acts">
            <button type="button" className="mp-btn small" disabled={busy === 'tape'} onClick={exportTape}>
              {busy === 'tape' ? 'Building…' : 'Download tape'}
            </button>
          </div>
        </div>
      </section>

      <section className="mp-card" aria-label="Sent">
        <h3 className="mp-h3">Sent</h3>
        {desk.sent?.length ? (
          <div className="mp-table-wrap">
            <table className="mp-table">
              <thead>
                <tr>
                  <th>What</th>
                  <th>Case</th>
                  <th>Fingerprint</th>
                  <th>When</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {desk.sent.slice(0, 8).map((s, i) => (
                  <tr key={`${s.at}-${i}`}>
                    <td>{s.what}</td>
                    <td>{s.label}</td>
                    <td className="mp-num">{String(s.hash).slice(0, 8)}</td>
                    <td>{new Date(s.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</td>
                    <td>{s.by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mp-empty">
            Nothing exported yet for this deal. Every export is logged with the case it came from, so a
            lender's question traces back to the exact numbers.
          </div>
        )}
      </section>
    </div>
  );
}
